import { createContext, createElement, FC, PropsWithChildren, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { modalDefinitions, ModalKeys } from './modalDefinitions';
import { useSearchParams } from 'react-router-dom';
import { Breakpoint, Dialog } from '@mui/material';
import * as qs from 'qs';

type Payload<T extends ModalKeys> = z.infer<(typeof modalDefinitions)[T]['payload']>;

type SetModalArgs<T extends ModalKeys> = {
	type: T;
	payload?: Payload<T>;
	callback?: () => void;
	maxWidth?: Breakpoint;
	fullScreen?: boolean;
};

type ModalContextProps = Readonly<{
	modalComponent: ReactNode;
	setModal: <T extends ModalKeys>(args: SetModalArgs<T>) => void;
	closeModal: () => void;
}>;

const ModalContext = createContext<ModalContextProps>({
	modalComponent: null,
	setModal: () => {},
	closeModal: () => {},
});

const [firstKey, ...restOfKeys] = Object.keys(modalDefinitions) as ModalKeys[];

const initialSchema = z.object({
	// This ensures that there will always be at least one key => https://zod.dev/?id=zod-enums
	type: z.enum([firstKey!, ...restOfKeys]).optional(),
	fullScreen: z.string().optional(),
	maxWidth: z.string().optional(),
});

const ModalContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [selectedModal, setSelectedModal] = useState<ReactNode>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const [maxWidth, setMaxWidth] = useState<Breakpoint>();
	const [fullScreen, setFullScreen] = useState<boolean | undefined>();
	const callbackRef = useRef<() => void | undefined>();

	useEffect(() => {
		const queryParams = qs.parse(searchParams.toString());
		if (!queryParams.modal && selectedModal != null) {
			closeModal();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	useEffect(() => {
		const queryParams = qs.parse(searchParams.toString());
		// Check if modal param exists within query string, so we do not check it when it is not necessary
		if (!queryParams.modal) {
			return;
		}

		const modalParams = queryParams.modal as any; // any is okay in this case as this const is validated twice below

		if (initialSchema.safeParse(modalParams).success) {
			const { component, payload } = modalDefinitions[modalParams.type as ModalKeys];
			// Extend initial schema (this operation is non destructive so initial schema remains the same) and safe parse
			// with modal schema
			const parsedResult = z.intersection(initialSchema, payload).safeParse(modalParams);
			if (parsedResult.success) {
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { type, fullScreen, maxWidth, ...props } = parsedResult.data;
				//@ts-expect-error
				setSelectedModal(createElement(component, props));
				setFullScreen(fullScreen === 'true');
				setMaxWidth(maxWidth as Breakpoint);
			} else {
				console.info('Parsing of query parameters failed', parsedResult.error);
			}
		}
	}, [searchParams]);

	// Get current query params and append modal params
	const setModal: ModalContextProps['setModal'] = ({ type, payload = {}, callback, maxWidth, fullScreen }) => {
		setSearchParams((prev) => {
			const queryParams = qs.parse(prev.toString());
			return qs.stringify({ ...queryParams, modal: { type, ...payload, fullScreen, maxWidth } });
		});

		callbackRef.current = callback;
	};

	// Remove modal query params, but persist those non related to modal
	const closeModal = () => {
		setSelectedModal(null);
		setSearchParams((prev) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { modal, ...rest } = qs.parse(prev.toString());
			return qs.stringify(rest);
		});
		setMaxWidth(undefined);
		callbackRef.current?.();
	};

	const modalComponent = (
		<Dialog fullScreen={fullScreen} fullWidth maxWidth={maxWidth} open={selectedModal !== null} onClose={closeModal}>
			{selectedModal}
		</Dialog>
	);

	return (
		<ModalContext.Provider
			value={{
				modalComponent,
				setModal,
				closeModal,
			}}
		>
			{children}
		</ModalContext.Provider>
	);
};

const useModalContext = (): ModalContextProps => useContext(ModalContext);

const ModalComponent: FC = () => {
	const { modalComponent } = useModalContext();
	return modalComponent;
};

export { ModalContextProvider, useModalContext, ModalComponent };
